/**
 * Show on board (Part 09 decision 6): the task's project, its task board
 * in Focus, and the task's pocket anchored inside the board. A board that
 * does not show in time leaves the pocket docked, as any open does.
 */
import {
  enterProject,
  type ProjectItemTarget,
} from "@/components/layout/cross-project-navigation";
import { ensureTaskboardFrame } from "@/desktop/onboarding/workspace-frames/ensure-taskboard-frame";
import { openFrames } from "@/lib/open-frames";
import {
  captureTaskOpenIntent,
  openTaskDetails,
} from "@/lib/right-panel-navigation";
import { boardFrameOnScreen } from "@/lib/task-pocket-placement";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import { selectWallContext } from "@/stores/wall-feed-store";

/** About a second of frames for the board to show in Focus. */
const BOARD_FRAMES = 60;

/**
 * The project's canonical board, created when it has none. Right after a
 * switch the frames store may not hold the project yet: the backend's
 * find-or-create answers and mounts the board.
 */
async function projectBoardId(
  projectId: string,
  switched: boolean,
): Promise<string> {
  const known = switched
    ? undefined
    : selectWallContext(useFramesStore.getState()).canonicalSingletons.get(
        "taskboard",
      );
  return known ?? ensureTaskboardFrame(projectId);
}

function boardShown(boardId: string): Promise<void> {
  return new Promise((resolve) => {
    let frames = BOARD_FRAMES;
    const check = () => {
      if (boardFrameOnScreen(boardId) || --frames <= 0) resolve();
      else requestAnimationFrame(check);
    };
    check();
  });
}

export async function showTaskOnBoard(task: ProjectItemTarget): Promise<void> {
  const shouldContinue = captureTaskOpenIntent();
  const entered = await enterProject(task, shouldContinue);
  const projectId = useProjectStore.getState().currentProject?.id;
  if (entered === "failed" || !shouldContinue() || !projectId) return;
  let boardId: string;
  try {
    boardId = await projectBoardId(projectId, entered === "switched");
  } catch {
    return;
  }
  if (!shouldContinue()) return;
  if (!openFrames({ frameIds: [boardId], origin: "navigate" })) return;
  await boardShown(boardId);
  await openTaskDetails(task.id, { boardFrameId: boardId, shouldContinue });
}
